/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Component: PollForm
 * File     : PollForm.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   Reusable React UI component: PollForm, composed within pages and other components.
 *   Exports: PollForm
 *   
 *
 * Dependencies:
 *   - ../../context/AuthContext
 *   - ../../services/api
 *   - ./MovieSearch
 *   - @mui/icons-material
 *   - react
 *
 * Usage:
 *   Import: import PollForm from '@/components/polls/PollForm';
 * -----------------------------------------------------------------------------
 */
import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { Save as SaveIcon, Add as AddIcon, Lock as LockIcon } from '@mui/icons-material';
import MovieSearch from './MovieSearch';
import { createPoll, listClubs } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const steps = ['Poll Details', 'Add Movies', 'Review & Create'];

const PollForm = ({ onSuccess, initialMovies = [] }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [pollData, setPollData] = useState({
    title: '',
    description: '',
    duration: 1440, // minutes (default 24h)
    movies: initialMovies,
  });
  const [errors, setErrors] = useState({});
  const [clubs, setClubs] = useState([]);
  const [clubId, setClubId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const requiresHostPlan = !!user && user.role !== 'owner' && user.planStatus !== 'active';

  React.useEffect(() => {
    listClubs().then(r => setClubs(r.clubs || [])).catch(() => setClubs([]));
  }, []);

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      if (!pollData.title.trim()) newErrors.title = 'Title is required';
      if (!pollData.duration || pollData.duration < 5) newErrors.duration = 'Duration must be at least 5 minutes';
    }

    if (step === 1) {
      if (pollData.movies.length < 2) newErrors.movies = 'Please add at least 2 movies';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleInputChange = (field, value) => {
    setPollData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleMovieSelect = (movie, remove = false) => {
    const normalize = (s) => (s || '').toString().trim().toLowerCase();
    const uniqueKey = (m) => (m?.imdbID && m.imdbID !== 'N/A') ? m.imdbID : `${normalize(m?.Title)}|${normalize(m?.Year)}`;
    if (remove) {
      setPollData(prev => ({
        ...prev,
        movies: prev.movies.filter(m => uniqueKey(m) !== uniqueKey(movie))
      }));
    } else {
      setPollData(prev => ({
        ...prev,
        movies: [...prev.movies, movie]
      }));
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) return;

    setSubmitting(true);
    try {
      const endDate = new Date();
      endDate.setMinutes(endDate.getMinutes() + parseInt(pollData.duration));

      // Send only necessary fields
      const minimalMovies = (pollData.movies || []).filter(m => m && m.Title).map(m => ({
        Title: m.Title,
        Year: m.Year,
        Poster: m.Poster,
        Type: m.Type,
        imdbID: m.imdbID
      }));

      const poll = await createPoll({
        title: pollData.title,
        description: pollData.description,
        endDate: endDate.toISOString(),
        movies: minimalMovies,
        clubId: clubId || undefined,
        sendDiscordAlert: !!pollData.sendDiscordAlert,
      });

      onSuccess(poll);
    } catch (error) {
      const msg = error?.response?.data?.error || error.message || 'Failed to create poll';
      console.error('Failed to create poll:', msg);
      setErrors({ submit: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Poll Title"
              value={pollData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              error={!!errors.title}
              helperText={errors.title}
              fullWidth
              required
            />

            <TextField
              label="Description"
              value={pollData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              multiline
              rows={3}
              fullWidth
            />

            <FormControl fullWidth error={!!errors.duration}>
              <InputLabel>Voting Duration</InputLabel>
              <Select
                value={pollData.duration}
                label="Voting Duration"
                onChange={(e) => handleInputChange('duration', e.target.value)}
              >
                <MenuItem value={5}>5 minutes</MenuItem>
                <MenuItem value={30}>30 minutes</MenuItem>
                <MenuItem value={60}>1 hour</MenuItem>
                <MenuItem value={360}>6 hours</MenuItem>
                <MenuItem value={720}>12 hours</MenuItem>
                <MenuItem value={1440}>24 hours</MenuItem>
                <MenuItem value={2880}>48 hours</MenuItem>
                <MenuItem value={10080}>1 week</MenuItem>
              </Select>
              {errors.duration && <FormHelperText>{errors.duration}</FormHelperText>}
            </FormControl>

            {clubs.length > 0 && (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Club (optional)</InputLabel>
                <Select value={clubId} label="Club (optional)" onChange={(e) => setClubId(e.target.value)} disabled={requiresHostPlan}>
                  <MenuItem value="">None (public poll)</MenuItem>
                  {clubs.filter(c => c && c.id).map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {requiresHostPlan && (
              <FormHelperText sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LockIcon fontSize="inherit" /> Host plan required to create club polls
              </FormHelperText>
            )}
            {clubId ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input id="sendDiscordAlert" type="checkbox" checked={!!pollData.sendDiscordAlert} onChange={(e) => setPollData(prev => ({ ...prev, sendDiscordAlert: e.target.checked }))} />
                <label htmlFor="sendDiscordAlert">Send Discord Alert</label>
              </Box>
            ) : null}
          </Box>
        );

      case 1:
        return (
          <Box>
            <MovieSearch
              onMovieSelect={handleMovieSelect}
              selectedMovies={pollData.movies}
            />
            {errors.movies && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {errors.movies}
              </Typography>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Poll Details
            </Typography>
            <Typography gutterBottom><strong>Title:</strong> {pollData.title}</Typography>
            <Typography gutterBottom><strong>Description:</strong> {pollData.description}</Typography>
            <Typography gutterBottom>
              <strong>Duration:</strong> {pollData.duration < 60 ? `${pollData.duration} minutes` : `${Math.round(pollData.duration / 60)} hours`}
            </Typography>
            {clubId && (
              <Typography gutterBottom>
                <strong>Club:</strong> {clubs.find(c => c.id === clubId)?.name || 'Unknown'}
              </Typography>
            )}

            <Typography variant="h6" sx={{ mt: 2 }} gutterBottom>
              Selected Movies ({pollData.movies.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {pollData.movies.filter(movie => movie && movie.Title).map(movie => (
                <Paper key={(movie.imdbID && movie.imdbID !== 'N/A') ? movie.imdbID : `${movie.Title}|${movie.Year}`} sx={{ p: 1, minWidth: 120 }}>
                  <Typography variant="body2">
                    {movie.Title} ({movie.Year})
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        );

      default:
        return <Typography>Unknown step</Typography>;
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.filter(step => step).map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {renderStepContent(activeStep)}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
        >
          Back
        </Button>

        <Box>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={<SaveIcon />}
            >
              {submitting ? 'Creating...' : 'Create Poll'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<AddIcon />}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>

      {errors.submit && (
        <Typography color="error" sx={{ mt: 2 }}>
          {errors.submit}
        </Typography>
      )}
    </Paper>
  );
};

export default PollForm;
